<template>
  <div class="w-full flex flex-wrap">
    <div class="w-full max-w-6xl pb-6">
      <div class="bg-red-300 border-2 border-slipmat-800 p-6 text-lg md:hidden mb-16">
        <span class="font-bold">Warning:</span> this is a specialized tool that is not intended to
        be used with small screens. Please use a tablet or a desktop browser for proper experience.
      </div>

      <h1 class="font-bold text-3xl mt-4 mb-2">EventPlanner 0.1a</h1>

      <p class="text-base text-gray-500">
        Event timezone:
        <span
          class="inline-flex leading-5 font-semibold rounded-full bg-gray-100 text-gray-700 cursor-pointer"
          @click="setTimezone({ tz: 'UTC', offset: 0 })"
          >UTC (+0)
        </span>
      </p>

      <p class="mb-3 text-base text-gray-500">
        Active timezone:
        <span class="font-semibold text-gray-700">{{ timezone }} ({{ offsetDisplay }})</span>
      </p>

      <div class="flex w-full">
        <div class="w-full max-w-2xl pr-3">
          <h3 class="text-xl font-bold mt-4 mb-2 text-gray-700">Time slots for Main Stage</h3>

          <time-slot-list
            v-if="store.timeSlotList"
            :times="store.timeSlotList"
            :timezone="timezone"
            :emitter="emitter"
            :store="store"
            v-on:change-tz="setTimezone"
          ></time-slot-list>
        </div>
        <div class="w-1/2 pl-3">
          <div class="sticky top-0">
            <h3 class="text-xl font-bold mt-4 mb-2 text-gray-700">Artists</h3>

            <artist-list
              :artists="unassignedArtists"
              :timezone="timezone"
              :emitter="emitter"
              :store="store"
              v-on:change-tz="setTimezone"
            ></artist-list>
          </div>
        </div>
      </div>
    </div>
    <div class="max-w-lg 3xl:border-l-4 3xl:border-gray-200 3xl:pl-6 pb-6">
      <about></about>
      <change-log></change-log>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, reactive, ref, computed } from 'vue'
import mitt from 'mitt'

import { getArtists, getTimeSlots } from '../api'

import TimeSlotList from './TimeSlotList.vue'
import ArtistList from './ArtistList.vue'
import ChangeLog from './ChangeLog.vue'
import About from './About.vue'

export default defineComponent({
  name: 'Home',
  components: {
    TimeSlotList,
    ArtistList,
    ChangeLog,
    About,
  },
  async setup() {
    const emitter = mitt()

    const store = reactive({
      artistList: await getArtists(),
      timeSlotList: await getTimeSlots(),
      selectedArtistId: -1,
      selectedSlotId: -1,
    })

    const unassignedArtists = computed(() =>
      store.artistList.filter(artist => artist.slotId === null),
    )
    const assignedArtists = computed(() =>
      store.artistList.filter(artist => artist.slotId !== null),
    )
    const timezone = ref('Europe/Helsinki')
    const utcOffset = ref(3)
    const setTimezone = ({ tz, offset }: { tz: string; offset: number }): void => {
      timezone.value = tz
      utcOffset.value = offset
      store.selectedSlotId = -1
      console.log('setting timezone to ', tz, offset)
    }
    emitter.on('set-tz', setTimezone)

    const offsetDisplay = computed(() => {
      if (utcOffset.value > -1) {
        return `+${utcOffset.value}`
      } else {
        return `${utcOffset.value}`
      }
    })

    const setSelectedArtist = (artistId: number): void => {
      store.selectedArtistId = artistId

      const artistIndex = store.artistList.findIndex(artist => artist.id === artistId)

      setTimezone({
        tz: store.artistList[artistIndex].timezone,
        offset: store.artistList[artistIndex].utcOffset,
      })
    }
    emitter.on('select-artist', setSelectedArtist)

    const assignArtistToSlot = ({
      artistId,
      slotId,
    }: {
      // prettier-ignore
      artistId: string;
      // prettier-ignore
      slotId: number;
    }): void => {
      const artistIndex = store.artistList.findIndex(artist => artist.id === parseInt(artistId))
      const timeSlotIndex = store.timeSlotList.findIndex(slot => slot.id === slotId)
      store.artistList[artistIndex].slotId = slotId
      store.timeSlotList[timeSlotIndex].artistId = parseInt(artistId)
      store.timeSlotList[timeSlotIndex].status = 'TENTATIVE'
      store.selectedSlotId = -1
    }
    emitter.on('assign-artist', assignArtistToSlot)

    const clearTimeSlot = (slotId: number): void => {
      const timeSlotIndex = store.timeSlotList.findIndex(slot => slot.id === slotId)
      const artistId = store.timeSlotList[timeSlotIndex].artistId
      const artistIndex = store.artistList.findIndex(artist => artist.id === artistId)

      store.timeSlotList[timeSlotIndex].artistId = null
      store.timeSlotList[timeSlotIndex].status = 'EMPTY'
      store.artistList[artistIndex].slotId = null
      store.selectedSlotId = -1
    }
    emitter.on('clear-slot', clearTimeSlot)

    const setTimeSlotStatus = ({
      slotId,
      status,
    }: {
      // prettier-ignore
      slotId: number;
      // prettier-ignore
      status: 'EMPTY' | 'TENTATIVE' | 'CONFIRMED';
    }): void => {
      const timeSlotIndex = store.timeSlotList.findIndex(slot => slot.id === slotId)
      store.timeSlotList[timeSlotIndex].status = status
      store.selectedSlotId = -1
    }
    emitter.on('set-slot-status', setTimeSlotStatus)

    const setSelectedSlot = (slotId: number): void => {
      store.selectedSlotId = slotId
    }
    emitter.on('select-slot', setSelectedSlot)

    return {
      unassignedArtists,
      assignedArtists,
      timezone,
      offsetDisplay,
      setTimezone,
      emitter,
      store,
    }
  },
})
</script>
